import React, { useCallback, useEffect, useState } from "react";
import {
    Card,
    Table,
    Tag,
    Button,
    Space,
    Popconfirm,
    message,
    Typography,
} from "antd";
import {
    ReloadOutlined,
    DeleteOutlined,
    CheckOutlined,
    CloseOutlined,
} from "@ant-design/icons";
import { api } from "../api";
import { fmtDate } from "../utils";

const { Paragraph } = Typography;

const STATUS_TAG = {
    pending: <Tag color="blue">Pending</Tag>,
    approved: <Tag color="green">Approved</Tag>,
    rejected: <Tag color="red">Rejected</Tag>,
};

const OrganizationsPage = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.listOrganizations();
            setItems(res.items);
        } catch (e) {
            message.error(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const setStatus = async (id, status) => {
        try {
            await api.setOrganizationStatus(id, status);
            setItems((prev) =>
                prev.map((it) => (it._id === id ? { ...it, status } : it))
            );
        } catch (e) {
            message.error(e.message);
        }
    };

    const remove = async (id) => {
        try {
            await api.deleteOrganization(id);
            setItems((prev) => prev.filter((it) => it._id !== id));
            message.success("Deleted");
        } catch (e) {
            message.error(e.message);
        }
    };

    const columns = [
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 110,
            render: (s) => STATUS_TAG[s] || <Tag>{s}</Tag>,
        },
        {
            title: "Organization",
            key: "org",
            render: (_, r) => (
                <div>
                    <strong>{r.orgName}</strong>
                    {r.website && (
                        <div className="admin-cell-sub">
                            <a
                                href={r.website}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {r.website}
                            </a>
                        </div>
                    )}
                    {r.city && (
                        <div
                            className="admin-cell-sub"
                            style={{ color: "#64748b" }}
                        >
                            {r.city}
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: "Contact",
            key: "contact",
            render: (_, r) => (
                <div>
                    <div>{r.contactName}</div>
                    <div>
                        <a href={`mailto:${r.email}`}>{r.email}</a>
                    </div>
                    <div className="admin-cell-sub" style={{ color: "#64748b" }}>
                        {r.phone}
                    </div>
                </div>
            ),
        },
        {
            title: "About",
            dataIndex: "description",
            key: "description",
            render: (m) =>
                m ? (
                    <Paragraph
                        style={{ margin: 0, maxWidth: 320 }}
                        ellipsis={{ rows: 3, expandable: true, symbol: "more" }}
                    >
                        {m}
                    </Paragraph>
                ) : (
                    <span style={{ color: "#94a3b8" }}>—</span>
                ),
        },
        {
            title: "Received",
            dataIndex: "createdAt",
            key: "createdAt",
            width: 170,
            render: (d) => fmtDate(d, true),
        },
        {
            title: "Actions",
            key: "actions",
            width: 210,
            render: (_, r) => (
                <Space wrap>
                    {r.status !== "approved" && (
                        <Button
                            size="small"
                            type="primary"
                            icon={<CheckOutlined />}
                            onClick={() => setStatus(r._id, "approved")}
                        >
                            Approve
                        </Button>
                    )}
                    {r.status !== "rejected" && (
                        <Button
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={() => setStatus(r._id, "rejected")}
                        >
                            Reject
                        </Button>
                    )}
                    <Popconfirm
                        title="Delete this application?"
                        onConfirm={() => remove(r._id)}
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <h1 className="admin-page-title">Organizations</h1>
            <Card
                variant="borderless"
                title="Partner applications"
                extra={
                    <Button
                        size="small"
                        icon={<ReloadOutlined />}
                        loading={loading}
                        onClick={load}
                    >
                        Refresh
                    </Button>
                }
            >
                <Table
                    rowKey="_id"
                    size="small"
                    loading={loading}
                    columns={columns}
                    dataSource={items}
                    scroll={{ x: "max-content" }}
                    pagination={{ pageSize: 10 }}
                />
            </Card>
        </div>
    );
};

export default OrganizationsPage;
